import {snapshotUnavailableShort} from "../../utils/reportingSnapshotAvailability.js";
import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi, ProjectApi} from "../../api/authority/generated/index";
import {useReportingError} from "../../api/utilities/useReportingError.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {Navigate} from "react-router-dom";
import ReportingInfoTooltip from "../../components/ReportingInfoTooltip.jsx";
import {calculatePnoPercent} from "../../utils/reportingMetricRatios.js";
import {hasReturnedMoneyMetric} from "../../utils/reportingFinancialCompleteness.js";
import ReportingSnapshotStatus from "./ReportingSnapshotStatus.jsx";
import {selectProfitabilityMetric, selectReportProfitabilityMetric} from "../../utils/reportingPartialProfitability.js";
import ReportingCostCoverageContext from "../../components/reporting/ReportingCostCoverageContext.jsx";
import {ProfitabilityMetricTooltip} from "../../components/reporting/ProfitabilityMetricTooltip.jsx";
import {selectedCentreSpendAttributionStatus} from "./CentreSpendAttributionNotice.jsx";
import CentreSpendAttributionNotice from "./CentreSpendAttributionNotice.jsx";
import {mediaSourceQualityBlocksMetrics, snapshotHasPublishedDays, snapshotPeriodValue, snapshotRowsForPublishedDays} from "../../utils/reportingSnapshotAvailability.js";
import {loadReportingResource, mergeReportingRowMetadata} from "../../utils/reportingIndependentRequest.js";
import {projectCentreDisplayName, shouldRedirectSingleCentreReport} from "../../utils/projectCentrePresentation.js";
import {
    ChannelMixColors,
    dividePercent,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatCurrencyItemTooltip,
    KpiCard,
    MetricHelpLabel,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
} from "./ReportingLab.jsx";

function centreName(centreWithDomains) {
    return projectCentreDisplayName(centreWithDomains);
}

function centreId(centreWithDomains) {
    const centre = centreWithDomains?.projectCentre ?? centreWithDomains;
    return centre?.id;
}

function centreDomains(centreWithDomains) {
    return (centreWithDomains?.domains ?? []).map((domain) => domain.hostName).filter(Boolean).join(", ");
}

export function shouldRenderProfitMetric(hasReturnedProfitField) {
    return hasReturnedProfitField;
}

export function centrePerformanceProfitMetric(performance, attributionStatus = null) {
    const partialProfitability = performance?.partialProfitability;
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(performance?.mediaSourceQuality);
    const standardSelection = selectProfitabilityMetric({
        fullValue: mediaQualityBlocked ? undefined : performance?.orderProfitNet,
        fullComplete: !mediaQualityBlocked && (partialProfitability === undefined ? performance?.orderProfitNet !== undefined : partialProfitability.complete === true),
        partialProfitability: !mediaQualityBlocked && attributionStatus === null ? partialProfitability : null,
        partialField: "knownProfitAfterAllMarketing",
    });
    return selectReportProfitabilityMetric({standardSelection, partialProfitability: mediaQualityBlocked ? null : partialProfitability, assignedField: "profitAfterAssignedMarketing"});
}

export function centreOverallProfitMetric(partialProfitability, snapshotIncomplete = false, attributionStatus = null, mediaSourceQuality = null) {
    const mediaQualityBlocked = mediaSourceQualityBlocksMetrics(mediaSourceQuality);
    const usableScope = !snapshotIncomplete && attributionStatus === null && !mediaQualityBlocked;
    const standardSelection = selectProfitabilityMetric({
        fullValue: usableScope ? partialProfitability?.knownProfitAfterAllMarketing : undefined,
        fullComplete: usableScope && partialProfitability?.complete === true,
        partialProfitability: usableScope ? partialProfitability : null,
        partialField: "knownProfitAfterAllMarketing",
    });
    return selectReportProfitabilityMetric({standardSelection, partialProfitability: mediaQualityBlocked ? null : partialProfitability, assignedField: "profitAfterAssignedMarketing", snapshotIncomplete});
}

export function centrePerformancePno(performance) {
    if (mediaSourceQualityBlocksMetrics(performance?.mediaSourceQuality)) return null;
    return calculatePnoPercent(performance?.orderSpendMarketing?.amount, performance?.orderTurnoverNet?.amount);
}

export function centreAttributionStatusForMetric(value, status) {
    if (status === null || status === undefined) return null;
    const amount = typeof value === "number" ? value : value && typeof value === "object" && value.amount !== null && value.amount !== undefined
        ? Number(value.amount)
        : NaN;
    return Number.isFinite(amount) ? status : null;
}

export function centreCostCoverageCompleteness(partialProfitability) {
    return typeof partialProfitability?.complete === "boolean" ? partialProfitability.complete : undefined;
}

function CentreUnavailableAttributionInfo({status}) {
    const {t} = useTranslation();
    if (status === null || status === undefined) return null;
    return <ReportingInfoTooltip ariaLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}>
        {status === "unknown"
            ? t("reporting_lab.centre_spend_attribution.unknown_help", {defaultValue: "Úplnost přiřazení reklamních nákladů nelze ověřit, proto není výsledek po reklamě podle středisek dostupný."})
            : t("reporting_lab.centre_spend_attribution.unavailable_metric_help", {defaultValue: "Zisk po reklamě není dostupný, protože část reklamních nákladů nelze jednoznačně přiřadit."})}
    </ReportingInfoTooltip>;
}

export default function ReportingCentresLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [centres, setCentres] = useState([]);
    const [performanceByCentre, setPerformanceByCentre] = useState([]);
    const [snapshot, setSnapshot] = useState(null);
    const [centreSpendAttribution, setCentreSpendAttribution] = useState(null);
    const [overallPartialProfitability, setOverallPartialProfitability] = useState(undefined);
    const [overallMediaSourceQuality, setOverallMediaSourceQuality] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metadataLoading, setMetadataLoading] = useState(true);
    const [metadataError, setMetadataError] = useState(false);
    const [reportError, setReportError] = useState(false);
    const [metadataRetry, setMetadataRetry] = useState(0);
    const [reportRetry, setReportRetry] = useState(0);
    const {et, retentionExpired} = useReportingError(project.id, setLoading);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const visibleCentres = useMemo(() => centres
        .filter((centre) => (centresAllowListed ?? []).length === 0 || centresAllowListed.includes(centreId(centre)))
        .sort((a, b) => centreName(a).localeCompare(centreName(b), "cs")), [centres, centreFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setPerformanceByCentre([]);
        setSnapshot(null);
        setCentreSpendAttribution(null);
        setOverallPartialProfitability(undefined);
        setOverallMediaSourceQuality(null);

        const authorityConfiguration = getAuthorityConfiguration();
        const erpApi = new ErpCalculationApi(authorityConfiguration);

        setReportError(false);
        loadReportingResource({
            request: () => erpApi.shopPerformanceByCentre({
                projectId: project.id,
                erpFilter: {
                    since: sinceString,
                    until: untilString,
                    selectedCentreIds: centresAllowListed,
                    selectedDomainIds: domainsAllowListed,
                },
            }),
            isActive: () => active,
            onSuccess: (dimensionData) => {
                if (!active) {
                    return;
                }

                setSnapshot(dimensionData?.snapshot ?? null);
                setCentreSpendAttribution(dimensionData?.centreSpendAttribution ?? null);
                setOverallPartialProfitability(dimensionData?.partialProfitability);
                setOverallMediaSourceQuality(dimensionData?.mediaSourceQuality ?? null);
                setPerformanceByCentre((dimensionData?.rows ?? []).map((row) => ({
                    centre: {id: row.id, name: row.name},
                    id: row.id,
                    name: row.name,
                    performance: row.performance,
                    partialProfitability: row.partialProfitability,
                })));
            },
            onError: (error) => {
                if (!active) {
                    return;
                }

                setReportError(true);
                et(error).then((message) => { if (message) toast.error(message); });
            },
            onFinally: () => { if (active) setLoading(false); },
        });

        return () => {
            active = false;
        };
    }, [project?.id, sinceString, untilString, centreFilterKey, domainFilterKey, reportRetry]);

    useEffect(() => {
        if (!project?.id) return undefined;

        let active = true;
        setMetadataLoading(true);
        setMetadataError(false);
        setCentres([]);

        const projectApi = new ProjectApi(getAuthorityConfiguration());
        loadReportingResource({
            request: () => projectApi.listProjectCentres({projectId: project.id}),
            isActive: () => active,
            onSuccess: (nextCentres) => setCentres(nextCentres ?? []),
            onError: (error) => {
                setMetadataError(true);
                et(error).then((message) => { if (message) toast.error(message); });
            },
            onFinally: () => setMetadataLoading(false),
        });

        return () => { active = false; };
    }, [project?.id, metadataRetry]);

    const summaryUnavailable = !snapshotHasPublishedDays(snapshot);
    const selectedCentreFilter = (centresAllowListed ?? []).length > 0;
    const attributionStatus = loading ? null : selectedCentreSpendAttributionStatus(centreSpendAttribution, selectedCentreFilter, true);
    const rows = useMemo(() => snapshotRowsForPublishedDays(mergeReportingRowMetadata(
        performanceByCentre,
        centres,
        {getRowId: (row) => row.id, getMetadataId: centreId, property: "centre"},
    ), snapshot)
        .map(({centre, performance, partialProfitability}) => {
            const turnover = performance?.orderTurnoverNet;
            const metricPerformance = performance?.partialProfitability === undefined && partialProfitability !== undefined
                ? {...performance, partialProfitability}
                : performance;
            const profitSelection = centrePerformanceProfitMetric(metricPerformance, attributionStatus);
            const profit = profitSelection.value;
            const hasProfit = profit !== undefined;
            const marketing = performance?.orderSpendMarketing;

            return {
                id: centreId(centre),
                name: centreName(centre),
                domains: centreDomains(centre),
                turnover,
                profit,
                profitPartial: profitSelection.partial,
                profitUsesAssignedMarketing: profitSelection.assignedMarketing,
                profitEstimated: !profitSelection.partial && profit != null && metricPerformance?.partialProfitability?.quality === "estimated" && Number(metricPerformance?.partialProfitability?.estimatedLineCount) > 0,
                profitAllowed: profitSelection.allowed,
                partialProfitability: metricPerformance?.partialProfitability,
                marketing,
                orders: Number(performance?.orderAmountNet ?? 0),
                ...(hasProfit && !profitSelection.partial ? {margin: dividePercent(moneyAmount(profit), moneyAmount(turnover))} : {}),
                pno: centrePerformancePno(performance),
            };
        })
        .sort((a, b) => moneyAmount(b.turnover) - moneyAmount(a.turnover)), [performanceByCentre, centres, snapshot, attributionStatus]);

    const currency = rows.find((row) => row.turnover?.currency)?.turnover?.currency ?? projectCurrency(project);
    const totalTurnover = snapshotPeriodValue(rows.reduce((sum, row) => sum + moneyAmount(row.turnover), 0), snapshot);
    const hasProfit = hasReturnedMoneyMetric(rows, "profit");
    const totalProfitSelection = centreOverallProfitMetric(overallPartialProfitability, summaryUnavailable, attributionStatus, overallMediaSourceQuality);
    const totalProfit = totalProfitSelection.value == null ? null : moneyAmount(totalProfitSelection.value);
    const hasCompleteProfit = totalProfit !== null && !totalProfitSelection.partial;
    const hasPartialProfit = totalProfit !== null && totalProfitSelection.partial;
    const showProfitMetric = shouldRenderProfitMetric(totalProfitSelection.allowed);
    const totalOrders = rows.reduce((sum, row) => sum + row.orders, 0);
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    if (shouldRedirectSingleCentreReport({projectId: project?.id, centres, loading: metadataLoading, error: metadataError})) {
        return <Navigate replace to={`/project/${project.id}/reporting`}/>;
    }

    const turnoverOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...rows].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 180, overflow: "truncate"},
        },
        series: [
            {
                name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}),
                type: "bar",
                data: [...rows].reverse().map((row, index) => ({
                    value: moneyToMajor(row.turnover),
                    itemStyle: {color: ChannelMixColors[(rows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 26,
            },
        ],
    };

    const shareOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartPercent(params.percent)}`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: rows.map((row) => ({
                    name: row.name,
                    value: moneyToMajor(row.turnover),
                })),
            },
        ],
    };

    return <WideSecureLayout periodReport retentionExpired={retentionExpired}
        title={t("reporting_lab.pages.centres", {defaultValue: "Střediska"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
     intro={<ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.centres.title", {defaultValue: "K čemu slouží přehled středisek"})}
                >
                    {t("reporting_lab.descriptions.centres.text", {defaultValue: "Tržby, objednávky a zisk po reklamě rozděluje podle středisek. Ekonomické metriky vycházejí z dokončených objednávek."})}
                </ReportingPageDescription>}>
        <div className="grid grid-cols-12 gap-4">
                {metadataError && <div role="alert" className="col-span-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                    <span>{t("reporting_lab.centres.metadata_unavailable", {defaultValue: "Seznam středisek se nepodařilo načíst. Report může zobrazit dostupná data, ale názvy a počty středisek nemusí být úplné."})}</span>
                    <button type="button" className="rounded-md border border-amber-300 px-3 py-1.5 font-medium hover:bg-amber-100 dark:border-amber-800 dark:hover:bg-amber-900/40" onClick={() => setMetadataRetry((value) => value + 1)}>
                        {t("reporting_lab.marketing.retry", {defaultValue: "Zkusit znovu"})}
                    </button>
                </div>}
                {reportError && <div role="alert" className="col-span-12 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
                    <span>{t("reporting_lab.centres.report_unavailable", {defaultValue: "Report středisek se nepodařilo načíst. Hodnoty nejsou dostupné."})}</span>
                    <button type="button" className="rounded-md border border-rose-300 px-3 py-1.5 font-medium hover:bg-rose-100 dark:border-rose-800 dark:hover:bg-rose-900/40" onClick={() => setReportRetry((value) => value + 1)}>
                        {t("reporting_lab.marketing.retry", {defaultValue: "Zkusit znovu"})}
                    </button>
                </div>}
                {!reportError && <>
                <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={overallMediaSourceQuality}/>
                <CentreSpendAttributionNotice attribution={centreSpendAttribution} requireAttribution loading={loading}/>
                <ReportingCostCoverageContext data={overallPartialProfitability} reportContext="centres" loading={loading}/>
                </>}

                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading || metadataLoading} label={t("reporting_lab.centres.centres", {defaultValue: "Střediska"})} value={loading || metadataLoading || metadataError || reportError ? "—" : renderNumber(visibleCentres.length)}/>
                </div>
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.revenue", {defaultValue: "Obrat"})} value={loading || totalTurnover == null ? "—" : renderCurrencyValue({amount: totalTurnover, currency}, true)}/>
                </div>
                {showProfitMetric && <div className="col-span-12 md:col-span-3">
                    <KpiCard
                        loading={loading}
                        label={totalProfitSelection.partial ? t("reporting_lab.kpi.partial_profit", {defaultValue: "Dílčí hrubý zisk po reklamě"}) : t("reporting_lab.kpi.profit", {defaultValue: "Hrubý zisk po reklamě"})}
                        value={loading || totalProfit === null ? "—" : renderCurrencyValue(totalProfitSelection.value, true)}
                        meta={loading ? "—" : hasCompleteProfit || hasPartialProfit ? null : summaryUnavailable ? snapshotUnavailableShort(snapshot, t) : attributionStatus === "unknown" ? t("reporting_lab.centre_spend_attribution.selected_unknown_short", {defaultValue: "Úplnost souhrnu vybraných středisek nelze ověřit"}) : attributionStatus === "incomplete" ? selectedCentreFilter ? t("reporting_lab.centre_spend_attribution.selected_short", {defaultValue: "Souhrn vybraných středisek je neúplný"}) : t("reporting_lab.centre_spend_attribution.incomplete_short", {defaultValue: "Neúplné přiřazení nákladů"}) : t("reporting_lab.cost_quality.incomplete_short", {defaultValue: "Neúplné náklady"})}
                        metaTone={!loading && !hasCompleteProfit && !hasPartialProfit ? "unavailable" : "default"}
                        info={<><ProfitabilityMetricTooltip metric="profit" partialProfitability={totalProfitSelection.partialProfitability ?? overallPartialProfitability} assignedResult={totalProfitSelection.assignedMarketing ? totalProfitSelection.partialProfitability?.assignedMarketingResult : null} assignedScope={rows.some((row) => row.profitUsesAssignedMarketing) && !totalProfitSelection.assignedMarketing} attributionStatus={attributionStatus} rowScopes={rows.map((row) => row.partialProfitability)} t={t}/>{!hasCompleteProfit && !hasPartialProfit && !summaryUnavailable && <div className="mt-1">{attributionStatus === "unknown" ? t("reporting_lab.centre_spend_attribution.unknown_help", {defaultValue: "Odpověď neobsahuje údaje o úplnosti přiřazení ani o částkách. Souhrn vybraných středisek proto nelze ověřit."}) : attributionStatus === "incomplete" ? t("reporting_lab.centre_spend_attribution.selected", {defaultValue: "Výkon vybraných středisek ukazuje známý přiřazený mezisoučet. Nepřiřazené náklady nelze přiřadit k vybranému středisku, proto jsou jeho souhrnné PNO a zisk neúplné."}) : t("reporting_lab.cost_quality.incomplete", {defaultValue: "Část nákladových podkladů chybí nebo není ověřena. Hrubý zisk po reklamě proto nelze za celé období spolehlivě vyčíslit."})}</div>}</>}
                        infoLabel={t("reporting_lab.kpi.info_label", {defaultValue: "Vysvětlení metriky"})}
                        tone={totalProfit !== null && totalProfit < 0 ? "bad" : "default"}
                    />
                </div>}
                <div className="col-span-12 md:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.comparison.orders", {defaultValue: "Objednávky"})} value={loading || summaryUnavailable ? "—" : renderNumber(totalOrders)}/>
                </div>

                <Panel loading={loading} title={t("reporting_lab.centres.revenue_by_centre", {defaultValue: "Obrat podle střediska"})} className="col-span-12 xl:col-span-7">
                    <EChart option={turnoverOption} className="h-96"/>
                </Panel>
                <Panel loading={loading} title={t("reporting_lab.centres.revenue_share", {defaultValue: "Podíl středisek na obratu"})} className="col-span-12 xl:col-span-5">
                    <EChart option={shareOption} className="h-96"/>
                </Panel>

                <Panel loading={loading} title={t("reporting_lab.centres.table", {defaultValue: "Přehled středisek"})} className="col-span-12">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                            <thead>
                            <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.centres.centre", {defaultValue: "Středisko"})}</th>
                                <th className="py-2 pr-4 font-semibold">{t("reporting_lab.pages.domains", {defaultValue: "Domény"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                {hasProfit && <th className="py-2 pr-4 text-right font-semibold"><MetricHelpLabel label={t("reporting_lab.table.contribution", {defaultValue: "Hrubý zisk po reklamě"})} tooltip={<ProfitabilityMetricTooltip metric="profit" partialProfitability={overallPartialProfitability} assignedResult={totalProfitSelection.assignedMarketing ? overallPartialProfitability?.assignedMarketingResult : null} assignedScope={rows.some((row) => row.profitUsesAssignedMarketing) && !totalProfitSelection.assignedMarketing} attributionStatus={attributionStatus} rowScopes={rows.map((row) => row.partialProfitability)} t={t}/>} /></th>}
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.comparison.pno", {defaultValue: "PNO"})}</th>
                                <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.orders.completed", {defaultValue: "Dokončené objednávky"})}</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                            {rows.map((row, index) => <tr key={row.id}>
                                <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.name}</td>
                                <td className="max-w-md py-3 pr-4 text-slate-600 dark:text-slate-300">{row.domains || "-"}</td>
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.turnover, true)}</td>
                                {hasProfit && <td className={`py-3 pr-4 text-right ${row.profit != null && moneyAmount(row.profit) < 0 ? "text-datixo-negative" : "text-slate-600 dark:text-slate-300"}`}><span className="inline-flex items-center justify-end gap-1">{row.profit == null ? "—" : renderCurrencyValue(row.profit, true)}{row.profit == null && <CentreUnavailableAttributionInfo status={attributionStatus}/>}</span></td>}
                                <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300"><span className="inline-flex items-center justify-end">{renderPercentValue(row.pno)}{row.pno == null && <CentreUnavailableAttributionInfo status={attributionStatus}/>}</span></td>
                                <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.orders)}</td>
                            </tr>)}
                            </tbody>
                        </table>
                    </div>
                    {!loading && !reportError && rows.length === 0 && <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">{t("reporting_lab.centres.empty", {defaultValue: "Pro vybrané období nejsou dostupná data středisek."})}</div>}
                </Panel>
            </div>
    </WideSecureLayout>;
}
